import { Box, Button, Flex, Input, Menu, Stack } from '@chakra-ui/react';
import { Plus, X } from '@phosphor-icons/react';
import { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';

import { fetchAPI } from '@/api/fetch';
import { apiCreateClient } from '@/api/services';
import {
  DialogActionTrigger,
  DialogBody,
  DialogCloseTrigger,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogRoot,
  DialogTitle,
  DialogTrigger
} from '@/components/ui/dialog';
import { Field } from '@/components/ui/field';
import { apiGetIndustries } from '@/api/services/industry.service';
import { CaretDown } from '@phosphor-icons/react/dist/ssr';

export default function AddNewClient({
  filled,
  onAddClient,
  disabled = false
}: {
  filled: boolean;
  onAddClient: () => void;
  disabled?: boolean;
}) {
  const [isLoading, setIsLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [industries, setIndustries] = useState<Industry[]>([]);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchIndustries = async () => {
      setIsLoading(true);

      try {
        const response = await fetchAPI<Industry[]>({
          request: () => apiGetIndustries()
        });
        if (response.success) {
          setError(null);
          setIndustries(response.data);
        } else {
          console.error('Failed to fetch industries:', response.message);
          setError('Failed to fetch industries. Please try again.');
        }
      } catch (error) {
        console.error('Failed to fetch industries:', error);
        setError('Failed to fetch industries. Please try again.');
      } finally {
        setIsLoading(false);
      }
    };

    if (open && industries.length === 0) {
      fetchIndustries();
    }
  }, [open, industries]);

  interface FormValues {
    companyName: string;
    industry: { value: string };
  }

  const {
    register,
    handleSubmit,
    control,
    reset,
    formState: { errors }
  } = useForm<FormValues>();

  const onSubmit = handleSubmit(async (formData) => {
    setIsLoading(true);
    try {
      const response = await fetchAPI<Client>({
        request: () =>
          apiCreateClient({
            name: formData.companyName,
            industry: formData.industry.value
          })
      });

      if (response.success) {
        onAddClient();
        setOpen(false);
      } else {
        console.error('Failed to create client:', response.message);
      }
    } catch (error) {
      console.error('Failed to create client:', error);
    } finally {
      setIsLoading(false);
    }
  });

  const handleDialogClose = () => {
    reset();
    setIsLoading(false);
  };

  return (
    <DialogRoot
      open={open}
      onOpenChange={(e) => setOpen(e.open)}
      closeOnInteractOutside={false}
      placement="center"
      motionPreset="slide-in-bottom"
      onExitComplete={handleDialogClose}
    >
      <DialogTrigger asChild>
        {filled ? (
          <Button
            w="240px"
            h="48px"
            borderRadius="8px"
            bg="primary.900"
            fontSize="16px"
            fontWeight={'regular'}
            disabled={disabled}
          >
            <Plus size={24} />
            Add new client
          </Button>
        ) : (
          <Button
            borderColor="primary.900"
            background="background.secondary"
            color="primary.900"
            borderRadius="8px"
            ml="16px"
            fontSize={'16px'}
            disabled={disabled}
          >
            <Plus size={24} />
            Add new
          </Button>
        )}
      </DialogTrigger>
      <DialogContent borderRadius="12px">
        <form onSubmit={onSubmit}>
          <DialogHeader>
            <DialogTitle>Add New Client</DialogTitle>
          </DialogHeader>
          <DialogBody>
            <Stack gap="4">
              <Field
                label={
                  <Box fontSize="16px">
                    <Box as="span" color="error.500">
                      *{' '}
                    </Box>
                    Company Name
                  </Box>
                }
                invalid={!!errors.companyName}
                errorText={
                  <Box fontSize="14px" color="error.700">
                    {errors.companyName?.message}
                  </Box>
                }
              >
                <Input
                  size="lg"
                  placeholder="Enter company name"
                  {...register('companyName', {
                    required: 'Company name is required'
                  })}
                  disabled={isLoading}
                />
              </Field>
              <Field
                label={
                  <Box fontSize="16px">
                    <Box as="span" color="error.500">
                      *{' '}
                    </Box>
                    Industry
                  </Box>
                }
                invalid={!!errors.industry}
                errorText={
                  <Box fontSize="14px" color="error.700">
                    {errors.industry?.message}
                  </Box>
                }
              >
                <Controller
                  name="industry"
                  control={control}
                  rules={{ required: 'Industry is required' }}
                  render={({ field }) => (
                    <Menu.Root>
                      <Menu.Trigger asChild>
                        <Button
                          variant="outline"
                          size="lg"
                          borderColor={errors.industry ? 'error.500' : 'neutrals.200'}
                          borderRadius="8px"
                          disabled={isLoading}
                          _disabled={{
                            bg: 'neutrals.300'
                          }}
                          w="100%"
                          aria-label="Select timezone"
                        >
                          <Flex justifyContent="space-between" alignItems="center" w="100%">
                            <Box
                              fontSize="16px"
                              color="neutrals.700"
                              overflow="hidden"
                              whiteSpace="nowrap"
                              textOverflow="ellipsis"
                            >
                              {field.value?.value || 'Select industry'}
                            </Box>
                            <Box as="span" ml="8px" mr="0">
                              <CaretDown size={16} aria-hidden="true" />
                            </Box>
                          </Flex>
                        </Button>
                      </Menu.Trigger>
                      <Menu.Positioner>
                        <Menu.Content
                          minW="10rem"
                          position="fixed"
                          zIndex={1500}
                          maxH="300px"
                          overflowY="auto"
                        >
                          <Menu.RadioItemGroup
                            value={field.value?.value || ''}
                            onValueChange={(value) => {
                              field.onChange(value);
                            }}
                          >
                            {industries.map(({ industry_id, name }) => (
                              <Menu.RadioItem
                                key={industry_id}
                                value={name}
                                fontSize="16px"
                                _hover={{
                                  cursor: 'pointer',
                                  bg: 'neutrals.100'
                                }}
                              >
                                {name}
                              </Menu.RadioItem>
                            ))}
                          </Menu.RadioItemGroup>
                        </Menu.Content>
                      </Menu.Positioner>
                    </Menu.Root>
                  )}
                />
              </Field>
              {error && (
                <Box fontSize="14px" color="error.500">
                  {error}
                </Box>
              )}
            </Stack>
          </DialogBody>
          <DialogFooter>
            <DialogActionTrigger asChild>
              <Button
                w="120px"
                borderColor="primary.900"
                background="background.secondary"
                color="primary.900"
                borderRadius="8px"
                fontSize="16px"
                disabled={isLoading}
              >
                Cancel
              </Button>
            </DialogActionTrigger>
            <Button
              type="submit"
              w="120px"
              background="primary.900"
              color="neutrals.0"
              borderRadius="8px"
              fontSize="16px"
              loading={isLoading}
            >
              Save
            </Button>
          </DialogFooter>
        </form>
        <DialogCloseTrigger m="12px" disabled={isLoading}>
          <X size={32} weight="bold" />
        </DialogCloseTrigger>
      </DialogContent>
    </DialogRoot>
  );
}
